import * as ExcelJS from "exceljs";
import * as XLSX from "xlsx";

export type ExcelFileType = "xlsx" | "xls" | "csv";

interface ExportToExcelProps<T> {
  data: T[];
  excelheader?: string[];
  fileName: string;
  sheetName?: string;
  fileType?: ExcelFileType;
}

export const exportToExcel = async <
  T extends Record<string, string | number | boolean | Date | null>
>({
  data,
  excelheader,
  fileName,
  sheetName = "Sheet1",
  fileType = "xlsx",
}: ExportToExcelProps<T>) => {
  // console.log("report 2", data);
  if (!data || data.length === 0) {
    console.error("No data provided for export.");
    return;
  }

  if (excelheader && excelheader.length === 0) {
    // Determine headers: Use `excelheader` if provided; otherwise, extract keys from data
    const headers: string[] = Object.keys(data[0]);
    console.log("report 3", data);
    if (fileType === "csv") {
      // Prepare CSV Data (Ensuring headers are mapped properly)
      const csvData = [
        headers,
        ...data.map((row) =>
          (headers ?? []).map((header) => `${row[header as keyof T] ?? ""}`)
        ), // Map only known headers
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(
        csvData.filter((row): row is string[] => Array.isArray(row))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Convert to CSV format and trigger download
      const csvString = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csvString], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.csv`;
      link.click();
    } else {
      console.log("report 4", data);
      console.log("report 4 - ExcelJS processing started");

      // Use ExcelJS for XLSX/XLS export
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);

      // Add header row correctly (ensures headers appear in the first row)
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4F81BD" }, // Blue background
        };
      });
      console.log("report 5 - Adding data rows");

      // Add data rows, ensuring only specified headers are used
      data.forEach((row) => {
        console.log("Adding Row:", row); // Debug log to see the row data
        const rowData = (headers ?? []).map(
          (header) => row[header as keyof T] ?? ""
        );
        console.log("Mapped Row Data:", rowData); // Check mapped data for each row
        worksheet.addRow(rowData);
      });

      console.log("report 6 - Border styling");
      // Apply border styling
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };
        });
      });

      console.log("report 7 - Adjusting column widths");
      // Adjust column widths based on content
      worksheet.columns.forEach((column, i) => {
        const maxLength = Math.max(
          headers?.[i]?.length ?? 0,
          ...data.map((row) =>
            row[headers?.[i] ?? ""] != null
              ? row[headers?.[i] ?? ""]!.toString().length
              : 0
          )
        );
        column.width = maxLength + 2;
      });

      console.log("report 8 - File creation");
      // Create and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.${fileType}`;
      link.click();
    }
  } else {
    if (excelheader && excelheader.length > 0) {
      // Use provided excelheader directly as headers
      const headers = excelheader;
      // console.log("report 3 - Custom Headers:", headers);

      if (fileType === "csv") {
        // Prepare CSV Data (Ensuring headers are mapped properly)
        const csvData = [
          headers,
          ...data.map((row) =>
            headers.map((_, index) => {
              // Map custom header's index to corresponding value in the row
              // We don't use header names, just the order (index-based mapping)
              const dataValue = row[Object.keys(row)[index] as keyof T]; // Map to the correct data value based on index
              return dataValue !== undefined && dataValue !== null
                ? dataValue.toString()
                : "";
            })
          ), // Map only known headers
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(csvData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Convert to CSV format and trigger download
        const csvString = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csvString], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        link.click();
      } else {
        // console.log("report 4 - ExcelJS processing started");

        // Use ExcelJS for XLSX/XLS export
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        // Add header row correctly (ensures headers appear in the first row)
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "4F81BD" }, // Blue background
          };
        });

        // console.log("report 5 - Adding data rows");

        // Add data rows, ensuring only specified headers are used
        data.forEach((row) => {
          const rowData = headers.map((_, index) => {
            // Map the header index to the corresponding value in the row
            // Find the value in the row corresponding to the current index
            const dataValue = row[Object.keys(row)[index] as keyof T]; // Map based on index
            return dataValue !== undefined && dataValue !== null
              ? dataValue
              : "";
          });
          worksheet.addRow(rowData);
        });

        // console.log("report 6 - Border styling");
        // Apply border styling
        worksheet.eachRow((row) => {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "thin", color: { argb: "000000" } },
              left: { style: "thin", color: { argb: "000000" } },
              bottom: { style: "thin", color: { argb: "000000" } },
              right: { style: "thin", color: { argb: "000000" } },
            };
          });
        });

        // console.log("report 7 - Adjusting column widths");
        // Adjust column widths based on content
        worksheet.columns.forEach((column, i) => {
          const maxLength = Math.max(
            headers[i]?.length || 0,
            ...data.map((row) =>
              row[headers[i]] != null ? row[headers[i]]!.toString().length : 0
            )
          );
          column.width = maxLength + 2;
        });

        // console.log("report 8 - File creation");
        // Create and download the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.${fileType}`;
        link.click();
      }
    }
  }
};
